import { useEffect } from 'react';
import Hero from './components/Hero';
import SignatureMeaning from './components/SignatureMeaning';
import PlansAndPricing from './components/PlansAndPricing';
import BenefitsAndFeatures from './components/BenefitsAndFeatures';
import ComplianceAndRegulations from './components/ComplianceAndRegulations';
import ContactAndCTA from './components/ContactAndCTA';
import Footer from './components/Footer';
import { useSchemaOrg } from '../../utils/seo';

export default function HomePage() {
  // Schema.org structured data
  const schemaData = [
    {
      '@context': 'https://schema.org',
      '@type': 'MedicalOrganization',
      '@id': 'SITE_URL/#organization',
      name: 'Med-Care | Signature™',
      alternateName: 'Med-Care Signature',
      url: 'SITE_URL',
      logo: 'SITE_URL/logo.png',
      description: 'Premium healthcare membership offering precision care, personal access, and elevated medicine in Las Vegas, Nevada. Featuring unlimited telehealth, named provider relationships, and comprehensive lab panels.',
      telephone: '+17027230303',
      email: 'signature@med-careproviders.com',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '2121 E Flamingo Rd Ste 218',
        addressLocality: 'Las Vegas',
        addressRegion: 'NV',
        postalCode: '89119',
        addressCountry: 'US'
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 36.1147,
        longitude: -115.1357
      },
      areaServed: {
        '@type': 'State',
        name: 'Nevada'
      },
      medicalSpecialty: [
        'Primary Care',
        'Preventive Medicine',
        'Telehealth',
        'Concierge Medicine'
      ],
      priceRange: '$99-$999',
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '08:00',
          closes: '17:00'
        }
      ]
    },
    {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      '@id': 'SITE_URL/#localbusiness',
      name: 'Med-Care | Signature™',
      image: 'SITE_URL/logo.png',
      url: 'SITE_URL',
      telephone: '+17027230303',
      email: 'signature@med-careproviders.com',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '2121 E Flamingo Rd Ste 218',
        addressLocality: 'Las Vegas',
        addressRegion: 'NV',
        postalCode: '89119',
        addressCountry: 'US'
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 36.1147,
        longitude: -115.1357
      },
      priceRange: '$99-$999',
      paymentAccepted: 'Cash, Credit Card, Debit Card'
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': 'SITE_URL/#website',
      url: 'SITE_URL',
      name: 'Med-Care | Signature™',
      description: 'Premium healthcare membership in Las Vegas, Nevada',
      publisher: {
        '@id': 'SITE_URL/#organization'
      }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': 'SITE_URL/#webpage',
      url: 'SITE_URL',
      name: 'Med-Care | Signature™ | Premium Healthcare Membership Las Vegas',
      description: 'Precision Care. Personal Access. Elevated Medicine. Premium healthcare membership for Nevada residents.',
      dateModified: '2026-04-11',
      isPartOf: {
        '@id': 'SITE_URL/#website'
      },
      about: {
        '@id': 'SITE_URL/#organization'
      },
      primaryImageOfPage: {
        '@type': 'ImageObject',
        url: 'SITE_URL/logo.png'
      }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What is Med-Care | Signature™?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Med-Care | Signature™ is a premium healthcare membership offering precision care, personal access, and elevated medicine. Members receive named provider relationships, direct clinical access, unlimited medically necessary telehealth, and comprehensive lab panels.'
          }
        },
        {
          '@type': 'Question',
          name: 'How much does Med-Care | Signature™ cost?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Med-Care | Signature™ offers three membership plans: Signature Essential at $297 quarterly, Signature Balance at $549 semi-annually (save $45), and Signature Elite at $999 annually (save $189). All plans include a 3-month commitment.'
          }
        },
        {
          '@type': 'Question',
          name: 'Who can join Med-Care | Signature™?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Med-Care | Signature™ is available exclusively to Nevada residents. It is designed for professionals who value time and clarity, families seeking consistent care, and individuals focused on prevention rather than crisis management.'
          }
        },
        {
          '@type': 'Question',
          name: 'What services are included in the membership?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Membership includes named provider assignment, direct clinical access via secure text, unlimited medically necessary telehealth, monthly in-clinic visit allocation, two comprehensive lab panels annually, after-hours clinical guidance, and priority prescriptions and care coordination.'
          }
        },
        {
          '@type': 'Question',
          name: 'Where is Med-Care | Signature™ located?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Med-Care | Signature™ is located at 2121 E Flamingo Rd Ste 218, Las Vegas, NV 89119. You can reach us at 702.723.0303 or signature@med-careproviders.com.'
          }
        }
      ]
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      serviceType: 'Healthcare Membership',
      provider: {
        '@id': 'SITE_URL/#organization'
      },
      areaServed: {
        '@type': 'State',
        name: 'Nevada'
      },
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Signature Membership Plans',
        itemListElement: [
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Signature Essential',
              description: 'Quarterly membership for proactive patients who value preparedness'
            },
            price: '297',
            priceCurrency: 'USD',
            priceSpecification: {
              '@type': 'UnitPriceSpecification',
              price: '297',
              priceCurrency: 'USD',
              referenceQuantity: {
                '@type': 'QuantitativeValue',
                value: '3',
                unitCode: 'MON'
              }
            }
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Signature Balance',
              description: 'Semi-annual membership for patients actively optimizing health and performance'
            },
            price: '549',
            priceCurrency: 'USD',
            priceSpecification: {
              '@type': 'UnitPriceSpecification',
              price: '549',
              priceCurrency: 'USD',
              referenceQuantity: {
                '@type': 'QuantitativeValue',
                value: '6',
                unitCode: 'MON'
              }
            }
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Signature Elite',
              description: 'Annual membership with highest level of access, value, and continuity'
            },
            price: '999',
            priceCurrency: 'USD',
            priceSpecification: {
              '@type': 'UnitPriceSpecification',
              price: '999',
              priceCurrency: 'USD',
              referenceQuantity: {
                '@type': 'QuantitativeValue',
                value: '12',
                unitCode: 'MON'
              }
            }
          }
        ]
      }
    }
  ];

  useSchemaOrg(schemaData);

  return (
    <div className="min-h-screen bg-white">
      <Hero />
      <SignatureMeaning />
      <PlansAndPricing />
      <BenefitsAndFeatures />
      <ComplianceAndRegulations />
      <ContactAndCTA />
      <Footer />
    </div>
  );
}
